import { useEffect, useState, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useTheme, Divider } from '@mui/material'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'

import { useCenterStore, useUserStore } from '@caradasstore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import {
  AdasBox,
  AdasButton,
  AdasContainer,
  AdasCard,
  AdasDivider,
  AdasPaper,
  AdasStack,
  AdasTypography,
  AdasGrid,
  AdasImage
} from '@components/wrapper-components'
import CaradasLogo from '../../assets/images/logo-caradas.png'
import { REDIRECT_URLS } from '../../constants'
import { ccidFormatter, isEmpty } from '../../utils/utils'
import { formatExistingTimeOrReturn } from 'utils/utils'
import { useDeviceQueries } from '@hooks/useDeviceQueries'
import { getUserList, getDashboardDetails, getCalibrationCenter } from '../../api/api'

export const HomePage = () => {
  const history = useHistory()
  const theme = useTheme()
  const { isMobile } = useDeviceQueries()

  const ccId = useCenterStore((s) => s.ccId)
  const centerDetail = useCenterStore((s) => s.centerDetail)
  const setCenterDetail = useCenterStore((s) => s.setCenterDetail)
  const setUserList = useCenterStore((s) => s.setUserList)
  const userEmail = useUserStore((s) => s.userEmail)
  const setCurrentUser = useUserStore((s) => s.setCurrentUser)
  const setLoading = useLoadingStore((s) => s.setLoading)

  const [dashboardData, setDashboardData] = useState()

  const styles = {
    summaryCard: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      height: 160,
      cursor: 'pointer',
      transition: theme.transitions.create(['box-shadow', 'transform'], {
        duration: theme.transitions.duration.short
      }),
      boxShadow: theme.shadows[1],
      '&:hover': {
        boxShadow: theme.shadows[4],
        backgroundColor: theme.palette.action.hover,
        transform: 'translateY(-2px)'
      }
    },
    quickActionButton: {
      height: { xs: 56, sm: 64, md: 72 },
      justifyContent: 'flex-start',
      textTransform: 'none',
      fontWeight: theme.typography.fontWeightBold,
      fontSize: { xs: '14px', sm: '15px' },
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.grey[50],
      whiteSpace: { xs: 'nowrap', lg: 'normal' },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        borderColor: theme.palette.divider
      }
    },
    cardBox: {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`
    },
    logoImage: {
      width: '100%',
      objectFit: 'contain'
    },
    logoBoxMobile: {
      width: 120,
      height: 80,
      display: { xs: 'flex', lg: 'none' }
    },
    logoBoxDesktop: {
      width: 160,
      height: 80,
      display: { xs: 'none', lg: 'flex' },
      justifyContent: 'flex-end'
    }
  }

  const fetchCalibrationCenter = useCallback(async () => {
    setLoading(true)
    try {
      const { status, data } = await getCalibrationCenter({ token: '', ccid: ccId })
      if (status === 200) setCenterDetail(data)
    } finally {
      setLoading(false)
    }
  }, [ccId, setCenterDetail, setLoading])

  const fetchUserList = useCallback(async () => {
    const response = await getUserList({ ccid: centerDetail.id || ccId })
    if (response?.status === 200) {
      setUserList(response.data)
      if (userEmail) {
        const user = response.data.find((item) => item.email?.toLowerCase() === userEmail?.toLowerCase())
        if (user) setCurrentUser(user)
      }
    }
  }, [centerDetail.id, ccId, setUserList, setCurrentUser, userEmail])

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await getDashboardDetails({ ccId })
      setDashboardData(response.data)
    } catch (err) {
      console.error(err)
    }
  }, [ccId])

  useEffect(() => {
    if (ccId && isEmpty(centerDetail)) {
      fetchCalibrationCenter()
    }
  }, [ccId, centerDetail, fetchCalibrationCenter])

  useEffect(() => {
    if (ccId) {
      fetchUserList()
      fetchDashboard()
    }
  }, [ccId, centerDetail.id, fetchUserList, fetchDashboard])

  const redirect = (url) => () => history.push(url)

  const cardDefinitions = useMemo(
    () => [
      {
        title: "TODAY'S ACTIVITY",
        value: dashboardData?.appointments?.length || 0,
        subtitle: 'Appointments',
        redirectTo: redirect('/scheduler')
      },
      {
        title: 'AWAITING RESPONSE',
        value: dashboardData?.openQuotes ?? 0,
        subtitle: 'Open Quotes',
        redirectTo: redirect('/quotes')
      },
      {
        title: 'ACTIVE JOBS',
        value: dashboardData?.openWorkOrders ?? 0,
        subtitle: 'Open Work Orders',
        redirectTo: redirect('/workorders')
      },
      {
        title: 'READY TO CLOSE',
        value: dashboardData?.openInvoices ?? 0,
        subtitle: 'Open Invoices',
        redirectTo: redirect('/invoices')
      }
    ],
    [dashboardData]
  )

  const SummaryCard = ({ title, value, subtitle, redirectTo }) => (
    <AdasCard sx={styles.summaryCard} onClick={redirectTo}>
      <AdasTypography className='body1' color={theme.palette.secondary.dark} sx={{ minHeight: 48 }}>
        {title}
      </AdasTypography>
      <AdasTypography variant='h4' sx={{ fontWeight: 700, my: 1 }}>
        {value}
      </AdasTypography>
      <AdasTypography variant='body2' sx={{ color: theme.palette.text.secondary }}>
        {subtitle}
      </AdasTypography>
    </AdasCard>
  )

  const QuickActionButton = ({ icon, label, onClick }) => (
    <AdasButton fullWidth variant='outlined' startIcon={icon} onClick={onClick} sx={styles.quickActionButton}>
      {label}
    </AdasButton>
  )

  return (
    <AdasContainer maxWidth='lg'>
      <AdasPaper elevation={0} sx={{ padding: 2 }}>
        <AdasBox>
          <AdasBox sx={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}>
            <AdasBox sx={{ flex: 1 }}>
              <AdasBox sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
                <AdasBox sx={{ display: 'flex', gap: 2 }}>
                  <AdasTypography sx={{ whiteSpace: 'nowrap' }}>{ccidFormatter(centerDetail?.id || ccId)}</AdasTypography>
                  <AdasTypography>{centerDetail?.name}</AdasTypography>
                </AdasBox>
                <AdasBox sx={styles.logoBoxMobile}>
                  <AdasImage src={centerDetail.image || CaradasLogo} alt='logo' sx={styles.logoImage} />
                </AdasBox>
              </AdasBox>
              <AdasTypography variant='h4' sx={{ fontWeight: 700 }}>
                Performance Dashboard
              </AdasTypography>
            </AdasBox>
            <AdasBox sx={styles.logoBoxDesktop}>
              <AdasImage src={centerDetail.image || CaradasLogo} alt='logo' sx={styles.logoImage} />
            </AdasBox>
          </AdasBox>

          {/* Summary Cards */}
          <AdasBox sx={{ padding: 2, mb: 3 }}>
            <AdasGrid container spacing={2}>
              {cardDefinitions.map((card, idx) => (
                <AdasGrid item xs={12} sm={6} md={3} key={idx}>
                  <SummaryCard {...card} />
                </AdasGrid>
              ))}
            </AdasGrid>
          </AdasBox>

          {/* Schedule and Quick Actions */}
          <AdasBox sx={{ padding: 2 }}>
            <AdasGrid container spacing={3}>
              <AdasGrid item xs={12} md={8}>
                <AdasCard sx={styles.cardBox}>
                  <AdasTypography variant='h6' sx={{ fontWeight: 600, mb: 1.5 }}>
                    Today&apos;s Schedule
                  </AdasTypography>
                  <Divider sx={{ my: 2, mx: -2 }} />
                  <AdasStack spacing={2} divider={<AdasDivider flexItem />} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {dashboardData?.appointments?.length > 0 ? (
                      dashboardData.appointments.map((item, i) => (
                        <AdasBox key={i} sx={{ display: 'flex', gap: 2 }}>
                          <AdasTypography variant='body1' sx={{ color: '#2563eb', fontWeight: 600, minWidth: 80 }}>
                            {
                              formatExistingTimeOrReturn(
                                item.appointment_date,
                                item.start_time,
                                item.organisation?.timezone_at || 'UTC',
                                item.duration
                              )?.startTime
                            }
                          </AdasTypography>
                          <AdasBox>
                            <AdasTypography variant='body1'>
                              <b>{`${item.vehicle?.year} ${item.vehicle?.make} ${item.vehicle?.model}`}</b>
                            </AdasTypography>
                            <AdasTypography variant='body2' sx={{ color: theme.palette.text.secondary }}>
                              {item.client?.full_name}
                            </AdasTypography>
                          </AdasBox>
                        </AdasBox>
                      ))
                    ) : (
                      <AdasBox sx={{ padding: 2, textAlign: 'center' }}>
                        <AdasTypography variant='body2' sx={{ color: theme.palette.text.disabled }}>
                          No appointments scheduled for today.
                        </AdasTypography>
                      </AdasBox>
                    )}
                  </AdasStack>
                </AdasCard>
              </AdasGrid>

              <AdasGrid item xs={12} md={4}>
                <AdasCard sx={styles.cardBox}>
                  <AdasTypography variant='h6' sx={{ fontWeight: 600, mb: 1.5 }}>
                    Quick Actions
                  </AdasTypography>
                  <Divider sx={{ my: 2, mx: -2 }} />
                  <AdasGrid container spacing={2}>
                    <AdasGrid item xs={12} xl={isMobile ? 12 : 6}>
                      <QuickActionButton
                        icon={<ReceiptLongOutlinedIcon />}
                        label='Create Work Order'
                        onClick={redirect(REDIRECT_URLS.NEW_WORK_ORDER)}
                      />
                    </AdasGrid>
                    <AdasGrid item xs={12} xl={isMobile ? 12 : 6}>
                      <QuickActionButton icon={<CalculateOutlinedIcon />} label='View Quotes' onClick={redirect('/quotes')} />
                    </AdasGrid>
                    <AdasGrid item xs={12} xl={isMobile ? 12 : 6}>
                      <QuickActionButton icon={<BuildOutlinedIcon />} label='Support' onClick={redirect('/support')} />
                    </AdasGrid>
                    <AdasGrid item xs={12} xl={isMobile ? 12 : 6}>
                      <QuickActionButton icon={<AttachMoneyOutlinedIcon />} label='View Invoices' onClick={redirect('/invoices')} />
                    </AdasGrid>
                  </AdasGrid>
                </AdasCard>
              </AdasGrid>
            </AdasGrid>
          </AdasBox>
        </AdasBox>
      </AdasPaper>
    </AdasContainer>
  )
}
